import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { Button, Stack } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import { FC, useState } from 'react';
import UserEventListItemEdit from './UserEventListItemEdit';
import UserEventListItem from './UserEventsListItem';

export type UserEventItem = {
  event: string;
  label: string;
};

type UserEventsManagerProps = {
  userEvents: UserEventItem[];
};

const UserEventsManager: FC<UserEventsManagerProps> = (props) => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { confirm } = useDialogs();

  const [openEdit, setOpenEdit] = useState(false);

  const confirmExists: ConfirmOptions = {
    title: 'Event already exists',
    description: `This user event already exists`,
    confirmationText: 'Ok',
    hideCancelButton: true
  };

  const getUpdatedNodes = (event: string): string[] => {
    const foundNodes: string[] = [];
    Object.entries(application.blocks.details).forEach(([nodeId, detail]) => {
      if (detail['name'] === event) {
        foundNodes.push(nodeId);
      }
    });
    application.blocks.event_handlers &&
      Object.entries(application.blocks.event_handlers).forEach(([nodeId, handler]) => {
        if (handler['UserEvent'] && handler['UserEvent']?.name === event) {
          foundNodes.push(nodeId);
        }
      });

    return foundNodes;
  };

  const handleOnSave = (newUserEvent: UserEventItem, oldEvent?: string) => {
    let updatedUserEvents = application.userevents ? structuredClone(application.userevents) : [];

    if (!oldEvent) {
      updatedUserEvents[updatedUserEvents.length] = { event: newUserEvent.event, label: newUserEvent.label };
    } else {
      updatedUserEvents = updatedUserEvents.map((userevent) => {
        if (userevent.event === oldEvent) {
          return { event: newUserEvent.event, label: newUserEvent.label };
        } else {
          return userevent;
        }
      });
    }
    if (
      updatedUserEvents.filter(
        (userevent) => userevent.event.toLocaleLowerCase() === newUserEvent.event.toLocaleLowerCase()
      ).length > 1
    ) {
      confirm(confirmExists);
      return;
    }

    setApplication({ ...application, userevents: updatedUserEvents });
    setUpdateNodes(getUpdatedNodes(newUserEvent.event));
  };

  return (
    <Stack gap={1} alignItems='start'>
      <List sx={{ width: '100%' }}>
        {props.userEvents.map((userEvent, index) => (
          <UserEventListItem
            key={`userevent.${userEvent.event}.${index}`}
            userEvent={userEvent}
            onSave={(newUserEvent) => handleOnSave(newUserEvent, userEvent.event)}
            onDelete={() => {
              const newUserEvents = application.userevents?.filter(
                (newUserEvent) => newUserEvent.event !== userEvent.event
              );
              const newNodes = getUpdatedNodes(userEvent.event);
              setApplication({ ...application, userevents: newUserEvents });
              setUpdateNodes(newNodes);
            }}
          />
        ))}
      </List>
      {openEdit && (
        <UserEventListItemEdit
          onClose={() => setOpenEdit(false)}
          onSave={(newUserEvent) => handleOnSave(newUserEvent)}
        />
      )}
      <Button variant='contained' color='primary' sx={{ ml: 2 }} onClick={() => setOpenEdit(true)}>
        Add new
      </Button>
    </Stack>
  );
};

export default UserEventsManager;
